import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import PurchaseTable from './PurchaseTable';
import ExportBar from './ExportBar';
import PropTypes from 'prop-types';

const PurchaseSection = ({
  title,
  fetchApi,
  exportApi,
  supplierId,
  supplierOptions,
  supplierLabel,
  onSupplierChange
}) => {
  const [data, setData] = useState([]);
  const [year, setYear] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 4 }, (_, i) => currentYear - i);
  const [columnType, setColumnType] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const supplierSelectOptions = useMemo(
    () =>
      supplierOptions.map(supplier => ({
        value: supplier.supplierId,
        label: `${supplier.supplierCode} - ${supplier.supplierName}`
      })),
    [supplierOptions]
  );
  const selectedSupplier =
    supplierSelectOptions.find(
      option => String(option.value) === String(supplierId)
    ) || null;

  useEffect(() => {
    if (year !== '' && supplierId) {
      loadData();
    }
  }, [year, columnType, supplierId]);

  useEffect(() => {
    setData([]);
  }, [supplierId]);

  const loadData = async () => {
    const res = await fetchApi(year, columnType, supplierId);
    if (res?.success) setData(res.data);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const from = fromDate.toLocaleDateString('en-CA'); // "2026-01-01"
      const to = toDate.toLocaleDateString('en-CA'); // "2026-01-02"
      await exportApi(from, to, columnType, supplierId);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div style={{ marginBottom: '35px' }}>
      <h5 style={{ fontWeight: 'bold' }} className="mb-3">
        {title.toUpperCase()}
      </h5>

      <Row className="mb-3">
        <Col xs={12} sm={6} md={4} lg={4}>
          <Form.Group controlId={`${title}-supplier`}>
            <Form.Label>{supplierLabel}</Form.Label>
            <Select
              inputId={`${title}-supplier`}
              isClearable
              options={supplierSelectOptions}
              value={selectedSupplier}
              onChange={option => onSupplierChange(option?.value || '')}
              placeholder="Select supplier..."
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({
                  ...base,
                  zIndex: 9999
                })
              }}
            />
          </Form.Group>
        </Col>

        <Col xs={12} sm={6} md={4} lg={3}>
          <Form.Group controlId={`${title}-year`}>
            <Form.Label>Year</Form.Label>
            <Form.Select value={year} onChange={e => setYear(e.target.value)}>
              <option value="">Select year</option>
              {years.map(y => (
                <option key={y}>{y}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col xs={12} sm={6} md={4} lg={3}>
          <Form.Group controlId={`${title}-date-type`}>
            <Form.Label>Date Type</Form.Label>
            <Form.Select
              value={columnType}
              onChange={e => setColumnType(Number(e.target.value))}
            >
              <option value={0}>Created Date</option>
              <option value={1}>Required Date</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Table */}
      <PurchaseTable data={data} />

      {/* Export */}
      <ExportBar
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
        onExport={handleExport}
        isExporting={isExporting}
        disabled={!supplierId}
      />
    </div>
  );
};

PurchaseSection.propTypes = {
  title: PropTypes.string.isRequired,
  fetchApi: PropTypes.func.isRequired,
  exportApi: PropTypes.func.isRequired,
  supplierId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  supplierOptions: PropTypes.arrayOf(PropTypes.object),
  supplierLabel: PropTypes.string,
  onSupplierChange: PropTypes.func
};

export default PurchaseSection;
